<template>
  <div
    class="book-flip"
    :class="{ 'is-single': single }"
    @pointerdown.capture="onTouch"
  >
    <div class="book-flip__stage" ref="stage">
      <div class="book-flip__controls" @pointerdown.stop>
        <button
          class="book-flip__button"
          data-auto
          type="button"
          v-if="auto?.shown"
          :aria-label="auto.state === 'playing' ? highlight.pause : highlight.play"
          @click="toggleAuto"
        >
          <SlideshowIcon :name="auto.state === 'playing' ? 'pause' : 'play'" />
        </button>
        <button
          class="book-flip__button"
          type="button"
          :aria-label="texts.previous"
          :disabled="pos === 0"
          @click="step(-1)"
        >
          <svg aria-hidden="true" viewBox="0 0 320 512">
            <use href="#chevron-left" />
          </svg>
        </button>
        <span aria-live="polite" class="book-flip__where" v-text="where" />
        <button
          class="book-flip__button"
          type="button"
          :aria-label="texts.next"
          :disabled="pos === last"
          @click="step(1)"
        >
          <svg aria-hidden="true" viewBox="0 0 320 512">
            <use href="#chevron-right" />
          </svg>
        </button>
        <button
          class="book-flip__button"
          type="button"
          :aria-expanded="sharing ? 'true' : 'false'"
          :aria-label="texts.shareBook"
          @click="sharing = !sharing"
        >
          <SlideshowIcon name="share" />
        </button>
        <button
          class="book-flip__button"
          data-larger
          type="button"
          v-if="larger"
          :aria-label="texts.larger"
          @click="emit('expand', spread)"
        >
          <SlideshowIcon name="full" />
        </button>
      </div>

      <!-- The same card as the slideshow's and the timeline's, for the link only. -->
      <ShareLinkCard
        class="book-flip__share"
        v-if="sharing"
        :name="deceasedName"
        :page-url="shareUrl"
        :report-copied="() => trackBook({ action: 'link_copied' })"
        :report-shared="
          (service) => trackBook({ action: 'link_shared', platform: service })
        "
        :tag="bookUrl"
        :title="texts.shareBook"
        @pointerdown.stop
      />
    </div>

    <ol class="book-flip__thumbs" v-if="strip" ref="stripEl">
      <li v-for="item in thumbs" :key="item.spread">
        <button
          class="book-flip__thumb"
          type="button"
          :aria-current="item.spread === spread ? 'true' : undefined"
          :aria-label="item.label"
          :class="[`is-${item.kind}`, { 'is-current': item.spread === spread }]"
          @click="toSpread(item.spread)"
        >
          <img alt="" decoding="async" loading="lazy" :src="item.src">
        </button>
      </li>
    </ol>
  </div>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef,
    watch,
  } from "vue";
  import { quickContent } from "../../composables/useSlideshow";
  import {
    type BookAutoplay,
    bookAutoplay,
  } from "../../highlights/book-autoplay";
  import { type BookFlip, createBookFlip } from "../../highlights/book-flip";
  import { addKeyTarget, shareOnScreen } from "../../highlights/book-keys";
  import type { BookImage, MemoryBook } from "../../highlights/memory-book";
  import { renewBookUrls } from "../../highlights/memory-book-panel";
  import { highlightTexts, memoryBookTexts } from "../../highlights/texts";
  import { trackBook } from "../../slideshow/track";
  import ShareLinkCard from "../Slideshow/ShareLinkCard.vue";
  import SlideshowIcon from "../Slideshow/SlideshowIcon.vue";

  /**
   * The memory book you turn (PRO-4397), in the section, the purchase panel and the large view.
   * The glass bar from Bildspel is there from the start: pause/play (when the book turns by
   * itself), ‹, where you are, ›, share, and larger. A thumbnail jumps without a turn.
   */
  const props = withDefaults(
    defineProps<{
      /** Turns by itself: `allowed` in the section, `manual` in the large view; none in the panel. */
      autoplay?: { allowed: boolean; manual?: boolean; start?: boolean } | null;
      book: MemoryBook;
      /** Who gets the arrow keys first. */
      keyOrder: number;
      /** Whether this book is in front of the reader, for the arrow keys. */
      keysActive?: () => boolean;
      /** The "View the book larger" button. */
      larger?: boolean;
      /** The section's id, for the shared link (`#minnesbok`). */
      sectionId: string;
      /** One page at a time (phones, the narrow panel). */
      single: boolean;
      /** Which preview size: 800 px on phones, 1600, 3200 in the large view. */
      size?: "small" | "normal" | "large";
      /** Opens on this spread (0 the cover). */
      startSpread?: number;
      strip?: boolean;
    }>(),
    {
      autoplay: null,
      keysActive: undefined,
      larger: false,
      size: "normal",
      startSpread: 0,
      strip: false,
    }
  );

  const emit = defineEmits<{
    expand: [spread: number];
    change: [spread: number];
  }>();

  const texts = memoryBookTexts();
  const highlight = highlightTexts();
  const reducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  /** utm_source of a shared book link, as `bildspel` is the slideshow's. */
  const BOOK_SHARE_SOURCE = "minnesbok";

  const stage = ref<HTMLElement | null>(null);
  const stripEl = ref<HTMLElement | null>(null);
  const flip = shallowRef<BookFlip | null>(null);
  const auto = shallowRef<BookAutoplay | null>(null);
  const pos = ref(0);
  const last = ref(0);
  const spread = ref(0);
  const sharing = ref(false);
  let flipped = false;
  /** For the message that goes with the link. */
  const deceasedName = quickContent()?.deceased.name ?? "";

  const spreads = computed(() => props.book.spreads.length);
  const backSpread = computed(() => (props.book.back ? spreads.value + 1 : -1));

  function srcOf(image: BookImage, size = props.size): string {
    if (size === "small") {
      return image.small ?? image.url;
    }
    return size === "large" ? (image.large ?? image.url) : image.url;
  }

  const sheets = computed(() => ({
    back: props.book.back ? srcOf(props.book.back) : null,
    cover: srcOf(props.book.cover),
    spreads: props.book.spreads.map((s) => srcOf(s)),
  }));

  /** "Omslaget", "Uppslag 3 av 12", "Baksidan"; one page at a time, the spread it is in. */
  const where = computed(() => {
    if (spread.value === 0) {
      return texts.cover;
    }
    if (spread.value === backSpread.value) {
      return texts.backCover;
    }
    return sprintf(texts.spreadOf, spread.value, spreads.value);
  });

  const thumbs = computed(() => {
    const small = (image: BookImage) => srcOf(image, "small");
    const items = [
      {
        kind: "board",
        label: texts.cover,
        spread: 0,
        src: small(props.book.cover),
      },
      ...props.book.spreads.map((image, i) => ({
        kind: "spread",
        label: sprintf(texts.spread, i + 1),
        spread: i + 1,
        src: small(image),
      })),
    ];
    if (props.book.back) {
      items.push({
        kind: "board",
        label: texts.backCover,
        spread: spreads.value + 1,
        src: small(props.book.back),
      });
    }
    return items;
  });

  const shareUrl = computed(
    () =>
      `${window.location.origin}${window.location.pathname}?del=minnesbok${props.sectionId ? `#${props.sectionId}` : ""}`
  );

  function onChange(p: number, l: number, s: number) {
    pos.value = p;
    last.value = l;
    spread.value = s;
    emit("change", s);
    preload(s);
    if (p > 0 && !flipped) {
      flipped = true;
      trackBook({ action: "flip" });
    }
  }

  /**
   * The sheets either side load before they're turned to. The addresses are signed for an hour:
   * a refused one asks for the book again, which brings new ones.
   */
  const loaded = new Set<string>();
  function preload(at: number) {
    const all = [sheets.value.cover, ...sheets.value.spreads];
    if (sheets.value.back) {
      all.push(sheets.value.back);
    }
    for (const src of all.slice(Math.max(0, at - 1), at + 3)) {
      if (src && !loaded.has(src)) {
        loaded.add(src);
        const img = new Image();
        img.onerror = () => {
          loaded.delete(src);
          renewBookUrls();
        };
        img.src = src;
      }
    }
  }

  function build(atSpread: number) {
    auto.value?.destroy();
    flip.value?.destroy();
    if (!stage.value) {
      return;
    }
    const book = createBookFlip(stage.value, {
      onChange,
      reducedMotion,
      sheets: sheets.value,
      single: props.single,
    });
    flip.value = book;
    last.value = book.last;
    book.jump(book.toPos(atSpread));
    auto.value = props.autoplay
      ? bookAutoplay(book, stage.value, props.autoplay)
      : null;
  }

  function step(delta: -1 | 1) {
    flip.value?.go((flip.value?.pos ?? 0) + delta);
  }

  /** A thumbnail is a jump, not a page turn: straight to it with a short fade. */
  function toSpread(s: number) {
    const book = flip.value;
    if (!book) {
      return;
    }
    book.jump(book.toPos(s));
    const el = stage.value?.querySelector(".book-flip__book");
    if (el && !reducedMotion()) {
      el.animate([{ opacity: 0.35 }, { opacity: 1 }], {
        duration: 220,
        easing: "ease-out",
      });
    }
  }

  function toggleAuto() {
    const a = auto.value;
    if (a?.state === "playing") {
      a.stop();
    } else {
      a?.play();
    }
  }

  /** The first click, tap or drag stops the book turning by itself (its own button and Larger don't). */
  function onTouch(e: PointerEvent) {
    const target = e.target as Element;
    if (!target.closest("[data-auto], [data-larger]")) {
      auto.value?.stop();
    }
  }

  /** The book's link as it goes out through one channel: `?del=minnesbok` stays, tagged like the slideshow's. */
  function bookUrl(url: string, medium: string): string {
    try {
      const u = new URL(url);
      u.searchParams.set("utm_source", BOOK_SHARE_SOURCE);
      u.searchParams.set("utm_medium", medium);
      return u.toString();
    } catch {
      return url;
    }
  }

  function onDocumentDown(e: PointerEvent) {
    if (sharing.value && !stage.value?.contains(e.target as Node)) {
      sharing.value = false;
    }
  }

  // The current thumbnail scrolls into view (the strip only, never the page).
  watch(spread, async () => {
    await nextTick();
    const row = stripEl.value;
    const thumb = row?.querySelector<HTMLElement>(".is-current");
    if (!(row && thumb)) {
      return;
    }
    const a = row.getBoundingClientRect();
    const r = thumb.getBoundingClientRect();
    if (r.left < a.left + 20 || r.right > a.right - 20) {
      row.scrollBy({
        behavior: reducedMotion() ? "auto" : "smooth",
        left: r.left - a.left - a.width / 2 + r.width / 2,
      });
    }
  });

  // One page at a time or two: a new book on the same spread.
  watch(
    () => props.single,
    () => build(spread.value)
  );
  // Another version of the book starts at its cover; renewed addresses keep the page.
  watch(
    () => props.book.shareId,
    () => build(0)
  );
  watch(sheets, (next) => {
    loaded.clear();
    flip.value?.setSheets(next);
  });

  let removeKeys: (() => void) | null = null;
  onMounted(() => {
    build(props.startSpread);
    removeKeys = addKeyTarget(
      {
        active: () =>
          props.keysActive
            ? props.keysActive()
            : shareOnScreen(stage.value) > 0.5,
        step: (delta) => {
          auto.value?.stop();
          step(delta);
        },
      },
      props.keyOrder
    );
    document.addEventListener("pointerdown", onDocumentDown);
  });

  onBeforeUnmount(() => {
    removeKeys?.();
    auto.value?.destroy();
    flip.value?.destroy();
    document.removeEventListener("pointerdown", onDocumentDown);
  });

  defineExpose({
    /** Open the cover with a turn (the link to the section). */
    open() {
      if (flip.value?.pos === 0) {
        flip.value.go(1);
      }
    },
    play: (delay?: number) => auto.value?.play(delay),
    get playing() {
      return auto.value?.state === "playing";
    },
    get spread() {
      return spread.value;
    },
    stop: () => auto.value?.stop(),
    toSpread(s: number) {
      flip.value?.jump(flip.value.toPos(s));
    },
  });
</script>
